import * as React from 'react';
import {styled, Theme, CSSObject} from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, {AppBarProps as MuiAppBarProps} from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import {useLocation} from 'react-router-dom'
import IconButton from '@mui/material/IconButton';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {Link} from "react-router-dom";
import {Badge, Container, Menu, MenuItem, useMediaQuery, useTheme} from '@mui/material';
import {AccountCircle, Notifications} from "@mui/icons-material";
import Auth from "../../utils/Auth";
import {ReactComponent as HalfLogo} from "../../assets/vk_half.svg";
import {ReactComponent as Logo} from "../../assets/vk_logo_white.svg";

import {
    DashboardOutlined,
    GoldOutlined,
    ReconciliationOutlined,
    TeamOutlined,
    InboxOutlined,
    RocketOutlined,
    ClockCircleOutlined
} from "@ant-design/icons";
import {useIsRefinitiv} from "../../hooks/refinitiv";

const drawerWidth = 240;

interface DrawerFooterProps extends MuiAppBarProps {
    open?: boolean;
}

const DrawerFooter = styled('div', {
    shouldForwardProp: (prop) => prop !== 'open',
})<DrawerFooterProps>(({theme, open}) => ({
    textAlign: 'right',
    width: '100%',
    padding: '16px',
    ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({theme, open}) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: 'border-box',
            ...(!open && {
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                width: theme.spacing(9),
                [theme.breakpoints.up('sm')]: {
                    width: theme.spacing(9),
                },
            }),
        },
    }),
);

type MinHeight = {
    minHeight: number,
};

function useAppBarHeight(): number {
    const {
        mixins: {toolbar},
        breakpoints,
    } = useTheme();

    const toolbarDesktopQuery = breakpoints.up('sm');
    const toolbarLandscapeQuery = `${breakpoints.up('xs')} and (orientation: landscape)`;
    const isDesktop = useMediaQuery(toolbarDesktopQuery);
    const isLandscape = useMediaQuery(toolbarLandscapeQuery);

    let currentToolbarMinHeight;
    if (isDesktop) {
        currentToolbarMinHeight = toolbar[toolbarDesktopQuery];
    } else if (isLandscape) {
        currentToolbarMinHeight = toolbar[toolbarLandscapeQuery];
    } else {
        currentToolbarMinHeight = toolbar;
    }

    // If no toolbar is found at the current breakpoint, use the xs default.
    if (!currentToolbarMinHeight) {
        return 56;
    }

    return (currentToolbarMinHeight as MinHeight).minHeight;
}

const DEFAULT_LIST = [
    {
        'label': 'Dashboard',
        'to': '',
        'icon': <DashboardOutlined/>
    }, {
        'label': 'Organizations',
        'to': 'organization',
        'icon': <GoldOutlined/>
    }, {
        'label': 'Projects',
        'to': 'project',
        'icon': <ReconciliationOutlined/>
    }, {
        'label': 'Users',
        'to': 'user',
        'icon': <TeamOutlined/>
    },
];

const REFINITIV_ITEMS = [
    {
        'label': 'Dashboard',
        'to': '',
        'icon': <DashboardOutlined/>
    }, {
        'label': 'Recent Deliveries',
        'to': 'recent',
        'icon': <ClockCircleOutlined/>
    }, {
        'label': 'All Deliveries',
        'to': 'deliveries',
        'icon': <InboxOutlined/>
    }, {
        'label': 'Collection Status',
        'to': 'status',
        'icon': <RocketOutlined/>
    }, {
        'label': 'Users',
        'to': 'user',
        'icon': <TeamOutlined/>
    },
]

function AppContainer({children, ...props}: any) {
    const [open, setOpen] = React.useState(true);
    const [opacity, setOpacity] = React.useState(1);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const appBarHeight = useAppBarHeight();
    const isRefinitiv = useIsRefinitiv();
    const location = useLocation();

    const currentPage = location.pathname.replace('/', '');
    const menuItems = isRefinitiv ? REFINITIV_ITEMS : DEFAULT_LIST;
    const theme = useTheme();
    const handleDrawState = () => {
        setOpen(!open);

        setTimeout(() => {
            if (open) {
                setOpacity(0)
            } else {
                setOpacity(1)
            }

        }, theme.transitions.duration.enteringScreen / 4)
    };

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        Auth.logout(() => {
            window.location.reload();
        })
    }

    return (
        <Box sx={{display: 'flex'}}>
            <AppBar position="fixed" sx={{zIndex: (theme) => theme.zIndex.drawer + 1}}>
                <Toolbar>
                    <Box component={Logo} sx={{display: {xs: 'none', md: 'flex'}, mr: 2, maxHeight: '35px'}}/>
                    <Typography variant="h6" noWrap component="div" sx={{flexGrow: 1}}>
                        Client Portal
                    </Typography>
                    {
                        !isRefinitiv && (
                            <IconButton
                                size="large"
                                aria-label="show 17 new notifications"
                                color="inherit"
                            >
                                <Badge badgeContent={17} color="error">
                                    <Notifications/>
                                </Badge>
                            </IconButton>
                        )
                    }

                    <IconButton
                        size="large"
                        aria-label="account of current user"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        onClick={handleMenu}
                        color="inherit"
                    >
                        <AccountCircle/>
                    </IconButton>
                    <Menu
                        id="menu-appbar"
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        <MenuItem>
                            <Link to="/account" style={{'textDecoration': 'none', 'color': 'inherit'}}>
                                Account
                            </Link>
                        </MenuItem>
                        <MenuItem onClick={handleLogout}>Log Out</MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>
            <Drawer variant="permanent" open={open}>
                <Box sx={{
                    'paddingTop': `${appBarHeight}px`,
                    'display': 'flex',
                    'flex': 'auto',
                    'flexDirection': 'column',
                    'overflow': 'hidden'
                }}>
                    <List sx={{'flex': 'auto'}}>
                        {
                            menuItems.map((item: any) => {
                                const isCurrentPage = item.to === currentPage;
                                return (
                                    <Link key={item.label} to={item.to}
                                          style={{'textDecoration': 'none', 'color': 'inherit'}}>
                                        <ListItem
                                            // dense
                                            disablePadding
                                            sx={{
                                                color: (theme) => isCurrentPage ? `${theme.palette.primary.main}` : 'inherit',
                                                backgroundColor: (theme) => isCurrentPage && open ? `${theme.palette.primary.main}1c` : 'inherit',
                                                '&& .Mui-selected, && .Mui-selected:hover': {
                                                    backgroundColor: open ? '#e7f0fa' : 'inherit',
                                                    '&, & .MuiListItemIcon-root': {
                                                        color: (theme) => `${theme.palette.primary.main}`,
                                                    },
                                                },
                                                '& .MuiListItemButton-root:hover': {
                                                    backgroundColor: open ? '#e7f0fa' : 'inherit',
                                                },
                                            }}
                                        >
                                            <ListItemButton disableRipple>
                                                <ListItemIcon
                                                    sx={{
                                                        fontSize: '1.25rem',
                                                        backgroundColor: isCurrentPage && !open ? '#e7f0fa' : 'inherit',
                                                        color: (theme) => isCurrentPage ? `${theme.palette.primary.main}` : 'inherit',
                                                        opacity: isCurrentPage ? '100%' : '54%',
                                                        minWidth: 0,
                                                        mr: 1,
                                                        padding: '8px 8px',
                                                        borderRadius: '6px',
                                                        '&:hover': {
                                                            backgroundColor: '#e7f0fa',
                                                        }
                                                    }}>
                                                    {item.icon}
                                                </ListItemIcon>
                                                <ListItemText primary={item.label}
                                                              sx={{opacity: opacity, '&& span': {fontSize: '.95rem'}}}/>
                                            </ListItemButton>
                                        </ListItem>
                                    </Link>
                                )
                            })
                        }
                    </List>
                    <DrawerFooter>
                        <IconButton onClick={handleDrawState}>
                            {open ? <ChevronLeftIcon/> : <ChevronRightIcon/>}
                        </IconButton>
                    </DrawerFooter>
                </Box>
            </Drawer>
            <Box
                component="main"
                sx={{
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                            ? 'rgb(250, 250, 251)'
                            : theme.palette.grey[900],
                    flexGrow: 1,
                    height: '100vh',
                    overflow: 'auto',
                    pt: `${appBarHeight}px`
                }}>
                <Box sx={{
                    pt: {xs: 2, sm: 3, lg: 4},
                    pb: {xs: 2, sm: 3, lg: 4},
                    pr: {xs: 2, sm: 3, lg: 8},
                    pl: {xs: 2, sm: 3, lg: 8}
                }}>
                    <React.Suspense>
                        {children}
                    </React.Suspense>
                </Box>
            </Box>
        </Box>
    );
}

function PublicContainer({children}: any) {
    return (
        <Box sx={{
            display: 'flex',
            minHeight: '100vh',
            backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                    ? 'rgb(250, 250, 251)'
                    : theme.palette.grey[900],
        }}>
            <Box sx={{
                position: "absolute",
                filter: "blur(18px)",
                bottom: "0px",
            }}>
                <Box component={HalfLogo} sx={{
                    width: '100%',
                    height: {"xs": "calc(100vh - 300px)", "md": "calc(100vh - 75px)"},
                    maxHeight: '950px'
                }}/>
            </Box>
            <Container component="main" maxWidth="xs" sx={{zIndex: 1,}}>
                {children}
            </Container>
        </Box>
    )
}

export default function MainContainer({children}: any) {
    const isAuthenticated = Auth.isAuthenticated();

    let content = (<PublicContainer>{children}</PublicContainer>);
    if (isAuthenticated) {
        content = (<AppContainer>{children}</AppContainer>)
    }
    return content;
}